// Display formatting. Amounts stay integers in the smallest unit until here; what is shown is
// truncated, never rounded up, so the screen never promises more than the chain will carry.

import { fromBaseUnits } from "./validate";

const group = (whole: string) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/** `v` smallest units of a `decimals` token, for reading: thousands grouped, a few significant
 *  fraction digits (2 from 10 000, 4 from 1, else 4 significant), truncated. "—" for bad decimals. */
export function prettyUnits(v: string | bigint, decimals: number): string {
  const full = fromBaseUnits(v, decimals);
  if (full === "—") return full;
  const [whole = "0", frac = ""] = full.split(".");
  const w = BigInt(whole);
  let keep: number;
  if (w >= 10_000n) keep = 2;
  else if (w >= 1n) keep = 4;
  else {
    const lead = frac.match(/^0*/)![0].length;
    keep = Math.min(lead + 4, decimals);
  }
  const f = frac.slice(0, keep).replace(/0+$/, "");
  if (w === 0n && !f && frac.length) return `<0.${"0".repeat(Math.max(0, keep - 1))}1`;
  return f ? `${group(whole)}.${f}` : group(whole);
}

/** `0x1234…abcd` / `7xKX…3Fa9`. */
export const short = (s: string, n = 4) => (s.length > 2 * n + 3 ? `${s.slice(0, n + (s.startsWith("0x") ? 2 : 0))}…${s.slice(-n)}` : s);

/** "12 s", "3 min 4 s", "1 h 2 min". */
export function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ${s % 60} s`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
}

/** "just now", "5 min ago", "3 h ago", "2 d ago". */
export function timeAgo(at: number, now = Date.now()): string {
  const s = Math.floor((now - at) / 1000);
  if (s < 45) return "just now";
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))} min ago`;
  if (s < 86_400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86_400)} d ago`;
}

/** How many `dst` one `src` buys, from two smallest-unit amounts, for the "1 A ≈ x B" line. */
export function rate(srcAmount: string | bigint, srcDecimals: number, dstAmount: string | bigint, dstDecimals: number): string | null {
  const a = BigInt(srcAmount);
  if (a === 0n || srcDecimals < 0 || dstDecimals < 0) return null;
  // dst per src unit, with 18 extra digits of precision, as integers
  const scaled = (BigInt(dstAmount) * 10n ** BigInt(srcDecimals) * 10n ** 18n) / a;
  return prettyUnits(scaled, dstDecimals + 18);
}
